import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistoryTexts, useUndoSnapshot } from '../hooks/useUndo';
import { KeyboardKey } from '../lib/input';
import { HistoryIcon } from '../ui/icons';
import styles from './TopBar.module.css';

/** "14:32" of an epoch instant in the device's local time. */
function clockOf(ms: number): string {
  const date = new Date(ms);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * Top-bar button that opens a read-only list of the user's last 20 actions since the page was
 * opened (newest first): when, what, to which object and with what result; actions that are
 * currently undone are marked. A press outside or Esc closes it.
 */
export function HistoryButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const { state } = useUndoSnapshot();
  const texts = useHistoryTexts(state.history);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target) === true) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== KeyboardKey.Escape) return;
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  return (
    <div className={styles.historyRoot} ref={rootRef}>
      <button
        type="button"
        className={styles.iconButton}
        aria-label={t('undo.history.open')}
        title={t('undo.history.open')}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid="history-button"
        onClick={() => setOpen((current) => !current)}
      >
        <HistoryIcon />
      </button>
      {open ? (
        <section id={panelId} className={styles.historyPanel} aria-label={t('undo.history.title')} data-testid="history-panel">
          <h2 className={styles.historyTitle}>{t('undo.history.title')}</h2>
          <p className={styles.historyHint}>{t('undo.history.hint')}</p>
          {state.history.length === 0 ? (
            <p className={styles.historyEmpty} data-testid="history-empty">
              {t('undo.history.empty')}
            </p>
          ) : (
            <ol className={styles.historyList}>
              {state.history.map((item, index) => (
                <li key={`${item.atMs}-${index}`} data-testid="history-item" data-undone={item.undone}>
                  <span className={styles.historyTime}>{clockOf(item.atMs)}</span>
                  <span className={styles.historyText}>{texts[index]}</span>
                  {item.undone ? <span className={styles.historyUndone}>{t('undo.history.undone')}</span> : null}
                </li>
              ))}
            </ol>
          )}
        </section>
      ) : null}
    </div>
  );
}
